/**
 * T-S1-A W8+W9 · 订单模块常量 & 工具函数
 */
import type { OrderSource, OrderStatus } from "@/services/types/orders";

// ── Status labels ─────────────────────────────────────────────────────────────

export const ORDER_STATUS_LABEL: Record<OrderStatus, string> = {
  draft: "待确认",
  confirmed: "已确认",
  assigned: "已分配",
  settled: "已结算",
  cancelled: "已取消",
};

/** Tailwind color tone for status badge */
export const ORDER_STATUS_TONE: Record<OrderStatus, string> = {
  draft: "bg-gray-100 text-gray-600",
  confirmed: "bg-blue-100 text-blue-700",
  assigned: "bg-violet-100 text-violet-700",
  settled: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-red-100 text-red-600",
};

// ── Source labels (Q13) ───────────────────────────────────────────────────────

export const ORDER_SOURCE_LABEL: Record<OrderSource, string> = {
  api_push: "API 推送",
  xiaohongshu: "小红书",
  tiktok: "TikTok",
  douyin: "抖音",
  instagram: "Instagram",
  youtube: "YouTube",
  other_social: "其他社媒",
  self_website: "官网自营",
  phone_direct: "电话直订",
  walk_in: "上门散客",
  email: "邮件",
  manual: "手工录入",
  partner_portal: "同行站",
  website: "手机站",
  manual_partner: "同行代下单",   // MANUAL-ORD-1
  manual_customer: "散客",         // MANUAL-ORD-1
};

/** PARTNERFIX-1 · 订单渠道的中文名——**唯一出口**（订单列表 / 详情 / 日历 / 调度 / 工作台 / 产品订单都走它）。
 *  字典（上面 ORDER_SOURCE_LABEL）要和后端 models/order.py::ORDER_SOURCE_VALUES 一一对应（有测试核对）；
 *  字典里没有的值（后端以后新增枚举、旧数据里的怪值）显示「其它（原值）」，绝不显示空白或裸英文代码。 */
/** GYGUX-1 · 订单「渠道」显示：有来源平台的显示平台名（GetYourGuide / Viator …，没有显示名退回代码大写）；
 *  没有平台（同行站 / 手机站 / 手工 / api_push 但平台为空）走上面的 orderSourceLabel——api_push 平台为空仍显示「API 推送」。
 *  一处出口：订单列表 / 详情 / 日历 / 调度 / 工作台 / 产品订单都用它。 */
export function orderChannelLabel(o: { source: string | null | undefined; channel_platform?: string | null; channel_platform_name?: string | null }): string {
  if (o.channel_platform) return (o.channel_platform_name || "").trim() || o.channel_platform.toUpperCase();
  return orderSourceLabel(o.source);
}

export function orderSourceLabel(source: string | null | undefined): string {
  if (!source) return "其它（未记录）";
  return (ORDER_SOURCE_LABEL as Record<string, string>)[source] ?? `其它（${source}）`;
}

export const ORDER_SOURCE_OPTIONS: { value: OrderSource; label: string }[] = [
  { value: "manual", label: ORDER_SOURCE_LABEL.manual },
  { value: "phone_direct", label: ORDER_SOURCE_LABEL.phone_direct },
  { value: "walk_in", label: ORDER_SOURCE_LABEL.walk_in },
  { value: "xiaohongshu", label: ORDER_SOURCE_LABEL.xiaohongshu },
  { value: "tiktok", label: ORDER_SOURCE_LABEL.tiktok },
  { value: "douyin", label: ORDER_SOURCE_LABEL.douyin },
  { value: "instagram", label: ORDER_SOURCE_LABEL.instagram },
  { value: "youtube", label: ORDER_SOURCE_LABEL.youtube },
  { value: "other_social", label: ORDER_SOURCE_LABEL.other_social },
  { value: "self_website", label: ORDER_SOURCE_LABEL.self_website },
  { value: "email", label: ORDER_SOURCE_LABEL.email },
  { value: "api_push", label: ORDER_SOURCE_LABEL.api_push },
];

/** PARTNERFIX-1 · 订单列表「渠道」筛选：全部 14 个来源（含同行站 / 手机站——它们不是手工录单能选的，所以不在上面的 OPTIONS 里，但要能筛） */
export const ORDER_SOURCE_FILTER_OPTIONS: { value: OrderSource; label: string }[] = (Object.keys(ORDER_SOURCE_LABEL) as OrderSource[])
  .map((value) => ({ value, label: ORDER_SOURCE_LABEL[value] }));

// ── Status machine helpers ────────────────────────────────────────────────────

export const TERMINAL_STATUSES = new Set<OrderStatus>(["settled", "cancelled"]);

export function isTerminal(status: OrderStatus): boolean {
  return TERMINAL_STATUSES.has(status);
}

// ORDSTAT · voucher_issued 退役：结算前置改 assigned→settled。出团确认单是正交动作（非状态转换）。
export const NEXT_STATUS: Partial<Record<OrderStatus, OrderStatus>> = {
  draft: "confirmed",
  confirmed: "assigned",
  assigned: "settled",
};

export const NEXT_STATUS_ACTION_LABEL: Partial<Record<OrderStatus, string>> = {
  draft: "确认订单",
  confirmed: "分配资源",
  assigned: "完成结算",
};

// ── Money helpers ─────────────────────────────────────────────────────────────

/** 分 → 元字符串（历史符号 ¥，勿在订单页新引用，改用 centsToUsd） */
export function centsToYuan(cents: number | null | undefined): string {
  if (cents == null) return "—";
  return `¥${(cents / 100).toFixed(2)}`;
}

/**
 * 分 → 美元字符串（系统默认币种 USD · 2026-05-29 用户拍板）
 * 整数时不显示小数点，例：$1,280 而非 $1,280.00
 */
export function centsToUsd(cents: number | null | undefined): string {
  if (cents == null) return "—";
  const usd = cents / 100;
  if (usd % 1 === 0) {
    return `$${usd.toLocaleString("en-US")}`;
  }
  return `$${usd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** 利润率 → 百分比字符串 */
export function marginToPercent(margin: number | null | undefined): string {
  if (margin == null) return "—";
  return `${(margin * 100).toFixed(1)}%`;
}
